import { apiBase } from "./config";
import { getToken, sessionRejected } from "./auth";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get retryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 502;
  }
}

export interface ReqOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal | undefined;
  timeoutMs?: number;
  /** extra attempts for network errors and 502/503/504/429. Defaults to 2 for GET, 0 otherwise. */
  retries?: number;
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      window.clearTimeout(t);
      reject(new DOMException("aborted", "AbortError"));
    });
  });

const buildUrl = (path: string, query?: ReqOptions["query"]): string => {
  const q = new URLSearchParams();
  if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== "") q.set(k, String(v));
  const s = q.toString();
  return apiBase() + path + (s ? "?" + s : "");
};

const parseError = async (res: Response): Promise<ApiError> => {
  let code = "http_" + res.status;
  let message = res.statusText || "Request failed";
  try {
    const j = (await res.json()) as { error?: string; message?: string };
    if (j.error) code = j.error;
    if (j.message) message = j.message;
  } catch {
    /* not JSON */
  }
  return new ApiError(res.status, code, message);
};

/** JSON request with the bearer token, a timeout, bounded retries for safe failures, and sign-out on 401. */
export async function api<T = unknown>(path: string, opts: ReqOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const retries = opts.retries ?? (method === "GET" ? 2 : 0);
  let attempt = 0;
  for (;;) {
    const token = getToken();
    if (!token) throw new ApiError(401, "unauthorized", "Not signed in");
    const ctl = new AbortController();
    const timer = window.setTimeout(() => ctl.abort(new DOMException("timeout", "TimeoutError")), opts.timeoutMs ?? 15_000);
    const onAbort = () => ctl.abort(opts.signal?.reason);
    opts.signal?.addEventListener("abort", onAbort);
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}`, Accept: "application/json" };
      const init: RequestInit = { method, headers, signal: ctl.signal, credentials: "omit" };
      if (opts.body !== undefined) {
        headers["Content-Type"] = "application/json";
        init.body = JSON.stringify(opts.body);
      }
      const res = await fetch(buildUrl(path, opts.query), init);
      if (res.status === 401) {
        sessionRejected();
        throw new ApiError(401, "unauthorized", "Your session has ended. Sign in again.");
      }
      if (!res.ok) throw await parseError(res);
      if (res.status === 204) return undefined as T;
      const text = await res.text();
      return (text ? JSON.parse(text) : undefined) as T;
    } catch (e) {
      if (opts.signal?.aborted) throw e;
      const err = e instanceof ApiError ? e : new ApiError(0, "network", "Can't reach the server");
      if (err.retryable && attempt < retries) {
        attempt++;
        await sleep(Math.min(400 * 2 ** attempt, 4000) * (0.5 + Math.random()), opts.signal);
        continue;
      }
      throw err;
    } finally {
      window.clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
    }
  }
}
